import { useEffect, useMemo, useRef, useState } from 'react';
import { useWorkQueueContext } from '../context/useWorkQueueContext.js';
import { getEventDetailPreview, eventHasDetailPreview } from '../../../services/index.js';
import { applyEventPreview } from '../utils/mapPreviewToDetail.js';
import { buildQuestionThreadSeed } from '../utils/buildQuestionThreadSeed.js';
import { EVENT_TYPE } from '../constants/eventTypes.js';

const getEmptyThread = () => ({ status: 'open', comments: [] });
const getEmptyList = () => [];

/**
 * Resolves a single event plus its thread, attestation, and audit trail for the
 * detail view (CT-5092).
 *
 * The base event, thread, attestation and audit trail come from the provider
 * (in-memory). DETAIL is enriched for real via the per-type GS preview endpoints
 * ({@link getEventDetailPreview}) — best-effort: a failure leaves the base event
 * intact and surfaces `error` without hiding the panel. `loading`/`error` track
 * that preview fetch and drive the RightPanel tab states.
 *
 * Thread / per-event audit / post-comment are blocked on CT-4867 (no endpoint),
 * so they stay provider-driven for the interim.
 */
export function useEventDetail(eventId) {
  const {
    getEventById,
    getPreviewEpoch = () => 0,
    getAuditTrail = getEmptyList,
    getAttestation = getEmptyList,
    getThread = getEmptyThread,
  } = useWorkQueueContext();

  const baseEvent = getEventById(eventId);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  // Set on mount AND cleared on unmount so StrictMode's mount→unmount→remount
  // leaves the guard `true` (matches WorkQueueProvider).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Re-fetch the preview only when the underlying entity identity changes, not on
  // every unrelated event mutation (status flips, owner changes, …).
  const previewKey = baseEvent
    ? [
        baseEvent.type,
        baseEvent.detail?.claimUuid ?? '',
        baseEvent.detail?.changeRequestUuid ?? '',
        baseEvent.detail?.knowledgebaseUuid ?? '',
        baseEvent.detail?.promotionUuid ?? '',
        String(getPreviewEpoch(eventId)),
      ].join(':')
    : null;

  useEffect(() => {
    setPreview(null);
    setError(null);
    // Skip the loading state for events that won't fetch a preview (CR/proposal/
    // task) so the RightPanel tabs don't flash a skeleton with no real fetch.
    if (!baseEvent || !eventHasDetailPreview(baseEvent)) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    getEventDetailPreview(baseEvent)
      .then((body) => {
        // Key the preview to the entity it was fetched for, so a response that
        // lands after the user switched events is never merged into the new one.
        if (active && mountedRef.current) setPreview({ key: previewKey, body });
      })
      .catch((err) => {
        if (active && mountedRef.current) setError(err?.message ?? 'Failed to load detail.');
      })
      .finally(() => {
        if (active && mountedRef.current) setLoading(false);
      });
    return () => {
      active = false;
    };
    // baseEvent is intentionally excluded — previewKey captures the identity that
    // should trigger a refetch; baseEvent is read fresh inside the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  // Only merge the preview when it belongs to the currently-selected entity —
  // guards against the stale preview of a previously-open event painting into
  // the new one on the first render after a switch.
  const event = useMemo(
    () => (preview && preview.key === previewKey ? applyEventPreview(baseEvent, preview.body) : baseEvent),
    [baseEvent, preview, previewKey],
  );

  const thread = useMemo(() => {
    const providerThread = getThread(eventId);
    if (providerThread.comments?.length) return providerThread;
    if (event?.type === EVENT_TYPE.QUESTION) {
      const seeded = buildQuestionThreadSeed(event);
      if (seeded.comments?.length) {
        return { ...seeded, status: providerThread.status ?? seeded.status };
      }
    }
    return providerThread;
  }, [event, eventId, getThread]);

  return useMemo(
    () => ({
      event,
      thread,
      attestation: getAttestation(eventId),
      auditTrail: getAuditTrail(eventId),
      loading,
      error,
    }),
    [event, thread, getAttestation, getAuditTrail, eventId, loading, error],
  );
}
